import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useListComponentsQuery } from '../api/authoring'
import { LISTING_EMPTY, LISTING_LAG } from '../model/labels'
import { routes } from '../model/routes'
import { EmptyState, Loading, QueryError } from '../ui/QueryState'
import { SITE_TYPE } from '../model/website'

/**
 * The tenant's sites.
 *
 * This is the one read that is not reachable by walking down from
 * something already known, which is why it is the client's boot: a token,
 * a tenant and a manifest do not name a site to open. It is served from
 * the tenant index over the projection — the **only** eventually
 * consistent read on this surface — so a site created a moment ago may
 * not be listed yet even though it reads by id immediately.
 *
 * **A tenant holding one site never sees this screen**: `TenantLanding`
 * redirects into the site rather than drawing a list of one. What is
 * left is the case this listing is actually for — an agency's account
 * with several, and a new account with none.
 */
export function TenantSites() {
  const { tenant = '' } = useParams()
  const { data, error, isLoading } = useListComponentsQuery({ tenant, type: SITE_TYPE })

  if (isLoading) return <Loading label="Listing this tenant's sites…" />
  if (error) return <QueryError error={error} title="Could not list this tenant's sites" />

  const sites = data?.components ?? []

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h1">Sites</Typography>
        <Typography color="text.secondary">
          Every site in <strong>{tenant}</strong>.
        </Typography>
      </Stack>

      {sites.length === 0 ? (
        <EmptyState title="No sites in this tenant">
          <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
            {LISTING_EMPTY}
          </Typography>
        </EmptyState>
      ) : (
        <Paper>
          <List disablePadding>
            {sites.map((site) => (
              <ListItemButton
                key={site.id}
                component={RouterLink}
                to={routes.site(tenant, site.id)}
              >
                <ListItemText
                  primary={site.label ?? site.id}
                  secondary={site.label ? site.id : 'Unnamed'}
                />
                <Chip size="small" variant="outlined" label={`r${site.revision}`} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {data?.cursor && (
        <Alert
          severity="info"
          action={
            <Button size="small" disabled>
              Load more
            </Button>
          }
        >
          There are more sites than this page shows. {LISTING_LAG}
        </Alert>
      )}
    </Stack>
  )
}
